import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import MakeTypeAhead from './components/makeTypeahead';
import ModelTypeAhead from './components/modelTypeahead';
import {
  ModelMakeStatuses,
  MAKE_MODEL_STATUS_MAP,
  NHTSA_STATUS_FILTER_VALUES,
  MAKE_MODEL_SOURCE_FILTER_VALUES,
  MAKE_MODEL_SOURCE_FILTER_MAP,
} from '../constants';


const MakeModelFilter = ({ handleFilterChange }) => {
  const [selectedMakeCode, setSelectedMakeCode] = useState('');

  const defaultFilter = 
      {
        operator: '$notIn',
        key: 'fleetStatus',
        value: ['0'],
      };


  const handleFilter = (updatedFilters) => {
    const filters = updatedFilters.flatMap((filter) => {
      // add default filter for all filters
      if (filter.key === 'bodyMake') {
        const makeCode = filter.value;
        setSelectedMakeCode(makeCode);
        return {
          ...filter,
          key: 'makeCode',
          value: makeCode,
        };
      }

      if (filter.key === 'bodyModel') {
        const modelCode = filter.value;
        return {
          ...filter,
          key: 'modelCode',
          value: modelCode,
        };
      }

      // The Fleet status options carry the status names ('Approved',
      // 'Rejected'); the fleetStatus column stores numeric codes, so map them
      // (e.g. 'Approved' -> 1, 'Rejected' -> 2).
      if (filter.key === 'fleetStatus') {
        return {
          operator: '$in',
          key: 'fleetStatus',
          value: filter.value.map((v) => String(MAKE_MODEL_STATUS_MAP[v])),
        };
      }

      // The backend filters nhtsaStatus by its numeric code (1 = Active,
      // 0 = Inactive), which the options already carry, so pass them through.
      if (filter.key === 'nhtsaStatus') {
        return {
          operator: '$in',
          key: 'nhtsaStatus',
          value: filter.value,
        };
      }

      // Expand the selected Source options into the model entrySource codes
      // each one represents (GSA Engineer covers 1, 2, 3 and 4). The backend
      // only accepts string values, so stringify the numeric codes.
      if (filter.key === 'entrySource') {
        return {
          operator: '$in',
          key: 'entrySource',
          value: filter.value
            .flatMap((v) => MAKE_MODEL_SOURCE_FILTER_MAP[v] || [])
            .map(String),
        };
      }

      return filter;
    });
    // if the filters doesn't have filter.key === 'approvalStatus'
    // then add the default filter
    if (!filters.some((filter) => filter.key === 'fleetStatus')) {
      filters.push(defaultFilter);
    }
    handleFilterChange(filters);
  };

  return (
    <FilterPanel.FilterPanel
      filterStructure={[
        {
          title: 'Make',
          key: 'bodyMake',
          id: 'bodyMake',
          operator: '$exact',
          component: MakeTypeAhead,
          value: '',
          isSingleValueFilter: true,
          permanent: false,
          expanded: true,
          hideClear: true,
          customFieldProps: {
            inputCharNum: 1,
            clearPanelFilterOnEmpty: true,
          },
        },
        {
          title: 'Model',
          key: 'bodyModel',
          id: 'bodyModel',
          operator: '$exact',
          component: ModelTypeAhead,
          value: '',
          isSingleValueFilter: true,
          expanded: true,
          hideClear: true,
          customFieldProps: { inputCharNum: 1, clearPanelFilterOnEmpty: true, makeCode: selectedMakeCode },
        },
        {
          title: 'Fleet status',
          key: 'fleetStatus',
          id: 'fleetStatus',
          type: 'multiselect',
          value: [],
          options: Object.keys(ModelMakeStatuses)
            .map((k) => ModelMakeStatuses[k])
            .filter((option) => (option.value !== 'Archived' && option.value !== 'PendingReview')),
          permanent: false,
          operator: '$in',
          hideClear: true,
          expanded: true,
        },
        {
          title: 'NHTSA status',
          key: 'nhtsaStatus',
          id: 'nhtsaStatus',
          type: 'multiselect',
          value: [],
          options: NHTSA_STATUS_FILTER_VALUES,
          permanent: false,
          operator: '$in',
          hideClear: true,
          expanded: true,
        },
        {
          title: 'Source',
          key: 'entrySource',
          id: 'entrySource',
          type: 'multiselect',
          value: [],
          options: MAKE_MODEL_SOURCE_FILTER_VALUES,
          permanent: false,
          operator: '$in',
          hideClear: true,
          expanded: true,
        },
      ]}
      clearButtonLabel="Reset All"
      showClearIcon
      setQueryFiltersState={handleFilter}
      model="AfpModel"
      order={[['id', 'ASC']]}
      subTitle="Apply filters"
      titleSeparator
      showSearchIcon
      retainFilters
    />
  );
};

MakeModelFilter.propTypes = {
  handleFilterChange: PropTypes.func.isRequired,
};

export default MakeModelFilter;
